import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle In-Dialog-Tour fuer den Bulk-Loeschen-Dialog
 * (modules/admin/component/bulk-delete-shifts-dialog). Wird via Hilfe-Button
 * (`<app-dialog-help-button>`) im Hero-Header manuell gestartet. Erklaert in
 * 6 Schritten: Welcome (destruktiv + alles vorausgewaehlt) → Tage → Abteilungen
 * & Mitarbeiter (UND-Verknuepfung, Schicht-Chips) → Erweiterte Optionen
 * ("Nur Entwuerfe loeschen", Accordion wird programmatisch geoeffnet) →
 * Vorschau (Live-Count via DryRun) → Loeschen (endgueltig + Benachrichtigung).
 *
 * Pattern analog `ADD_SHIFT_HELP_TOUR` / `AUTO_SUGGEST_HELP_TOUR`: erklaerungs-
 * getrieben (`nextCondition: 'manual'`), nicht formular-gebunden.
 *
 * Fachliche Quelle (Backend `BulkDeleteShiftsCommandHandler`): Filter
 * (Days/DepartmentIds/EmployeeIds/OnlyDrafts) sind UND-verknuepft; `null` = alle.
 * Hard-Delete (permanent). `OnlyDrafts=true` schliesst Published aus. Gestrichene
 * veroeffentlichte Schichten loesen je Mitarbeiter eine konsolidierte
 * Wochen-Benachrichtigung aus. Die Vorschau laeuft als DryRun.
 */

/**
 * Oeffnet die "Erweiterte Optionen"-Sektion via synthetischem Klick auf den
 * Toggle. Idempotent: prueft die `.open`-Klasse, klickt nur wenn geschlossen.
 */
function openAdvancedSection(): void {
  const section = document.querySelector<HTMLElement>(
    `[onboardingAnchor="${AnchorId.BulkDeleteAdvanced}"]`,
  );
  if (section && !section.classList.contains('open')) {
    section.querySelector<HTMLButtonElement>('.advanced-toggle')?.click();
  }
}

/** Schliesst die Sektion wieder (Cleanup nach Step-Verlassen). */
function closeAdvancedSection(): void {
  const section = document.querySelector<HTMLElement>(
    `[onboardingAnchor="${AnchorId.BulkDeleteAdvanced}"]`,
  );
  if (section && section.classList.contains('open')) {
    section.querySelector<HTMLButtonElement>('.advanced-toggle')?.click();
  }
}

export const BULK_DELETE_SHIFTS_HELP_TOUR: TourConfig = {
  id: TourFeature.BulkDeleteShiftsHelp,
  version: 1,
  title: 'BULK_DELETE_SHIFTS_HELP_TOUR_TITLE',
  description: 'BULK_DELETE_SHIFTS_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Help-Hub-Start: zur Schichtzuweisung navigieren. Der "Schichten
  // massenhaft loeschen"-Button sitzt seit dem Toolbar-Refactor im
  // Overflow-Menue (`fc-overflowMenuButton-button`) — kein deterministischer
  // 1-Klick-Opener. `route` only ist ohnehin die richtige UX fuer einen
  // destruktiven Dialog: User landet auf der richtigen Seite und oeffnet
  // das Menue + Item bewusst selbst.
  launchContext: {
    route: '/planner',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 60,
  // Bewusst 'feature-discovery' (NICHT 'contextual-help'): klassische Guided
  // Tour mit Spotlight + Voice-Over, nicht der HelpModeService mit (i)-Icons.
  // Begruendung siehe AUTO_SUGGEST_HELP_TOUR.
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'BULK_DELETE_SHIFTS_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          // Welcome erklaert den GANZEN Dialog — Spotlight am .bds-Wrapper.
          anchor: AnchorId.BulkDeleteDialog,
          title: 'TUTORIAL.BULK_DELETE_SHIFTS.WELCOME.TITLE',
          content: 'TUTORIAL.BULK_DELETE_SHIFTS.WELCOME.CONTENT',
          placement: 'auto',
          tone: 'warning',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'days',
          anchor: AnchorId.BulkDeleteDays,
          title: 'TUTORIAL.BULK_DELETE_SHIFTS.DAYS.TITLE',
          content: 'TUTORIAL.BULK_DELETE_SHIFTS.DAYS.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'filters',
          // Spotlight umfasst Mitarbeiter- UND Abteilungs-Sektion: beide bilden
          // gemeinsam (mit den Tagen) die UND-verknuepfte Loeschmenge. Popover
          // ankert an der Mitarbeiter-Sektion (dort liegen die Schicht-Chips).
          anchor: AnchorId.BulkDeleteEmployees,
          additionalSpotlightAnchors: [AnchorId.BulkDeleteDepartments],
          title: 'TUTORIAL.BULK_DELETE_SHIFTS.FILTERS.TITLE',
          content: 'TUTORIAL.BULK_DELETE_SHIFTS.FILTERS.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          // Mitarbeiter-/Abteilungs-Sektion sind *ngIf-gebunden; ohne uebergebene
          // Mitarbeiter wird der Anker nicht gerendert. DOM-realer Check statt
          // vermutetem Zustand (siehe CONTEXTUAL_HELP_PATTERN-Stolperfallen).
          skipIf: () =>
            !document.querySelector(
              `[onboardingAnchor="${AnchorId.BulkDeleteEmployees}"]`,
            ),
        },
        {
          id: 'advanced',
          anchor: AnchorId.BulkDeleteAdvanced,
          title: 'TUTORIAL.BULK_DELETE_SHIFTS.ADVANCED.TITLE',
          content: 'TUTORIAL.BULK_DELETE_SHIFTS.ADVANCED.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: openAdvancedSection,
          onAfterHide: closeAdvancedSection,
        },
        {
          id: 'preview',
          anchor: AnchorId.BulkDeletePreview,
          title: 'TUTORIAL.BULK_DELETE_SHIFTS.PREVIEW.TITLE',
          content: 'TUTORIAL.BULK_DELETE_SHIFTS.PREVIEW.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'confirm',
          anchor: AnchorId.BulkDeleteConfirm,
          title: 'TUTORIAL.BULK_DELETE_SHIFTS.CONFIRM.TITLE',
          content: 'TUTORIAL.BULK_DELETE_SHIFTS.CONFIRM.CONTENT',
          placement: 'top',
          tone: 'warning',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          // Letzter Step: sauberer Abschluss, kein Zurueck-Button.
          showPrevButton: false,
        },
      ],
    },
  ],
};
